import { LogManager } from '@src/utility/Logger';
import { Uploader } from '@src/utility/uploader';
import MD5 from 'crypto-js/md5';
import { z } from 'zod';

import type { KaraokeDataSerialized } from '@src/editor/types/karaoke';
import type { Video } from '@src/types.ts/story';
import type {
  VideoResourceMetadata,
  VideoResources,
} from '@src/types.ts/video';

export const VideoSourceSchema = z
  .object({
    width: z.number().optional(),
    height: z.number().optional(),
    frame_rate: z.number().optional(),
    duration: z.number().optional(),
    elements: z.array(z.any()).optional(),
  })
  .passthrough();

export const VideoTranscriptSnapshotSchema = z
  .object({
    elements: z.array(z.any()),
  })
  .passthrough();

export const VideoKaraokeDataSchema = z
  .object({
    clips: z.array(
      z.object({
        id: z.string(),
        transcriptTokenIds: z.array(z.string()),
        hiddenTranscriptTokenIds: z.array(z.string()).optional(),
        ignoredByFiltersIds: z.array(z.string()).optional(),
        startSecondsOffset: z.number().optional(),
        endSecondsOffset: z.number().optional(),
        karaokeConfigId: z.string().optional(),
      }),
    ),
    configs: z.array(z.tuple([z.string(), z.any()])),
    defaultConfigId: z.string().nullable(),
  })
  .passthrough();

export const VideoResourcesSchema = z
  .object({
    videoSourceSnapshot: z
      .object({
        // Either a directly consumable (signed) CloudFront URL OR a fileKey that we need to exchange for a signed URL.
        url: z.string().url().optional(),
        fileKey: z.string().min(1).optional(),
        fileName: z.string().optional(),
        md5Hash: z.string().optional(),
      })
      .passthrough()
      .optional(),
    transcriptionSnapshot: z
      .object({
        url: z.string().url().optional(),
        fileKey: z.string().min(1).optional(),
        fileName: z.string().optional(),
        md5Hash: z.string().optional(),
      })
      .passthrough()
      .optional(),
    karaokeData: z
      .object({
        url: z.string().url().optional(),
        fileKey: z.string().min(1).optional(),
        fileName: z.string().optional(),
        md5Hash: z.string().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();

type VideoResourceSchemaType = z.infer<typeof VideoResourcesSchema>;

const logger = LogManager.getLogger('service:VideoResourcesProvider');

/**
 * Fetches a video source from a specific URL
 *
 * @param videoSourceUrl URL to fetch video source from
 * @returns VideoSource json
 */
async function fetchJsonResourceFromUrl<Z extends z.ZodSchema>(
  resourceUrl: string,
  resourceSchema: Z,
): Promise<z.infer<Z>> {
  const jsonResponse = await fetch(resourceUrl);
  const json: unknown = await jsonResponse.json();
  const parsed = resourceSchema.safeParse(json);
  if (!parsed.success) {
    logger.warn('Resource schema validation failed', {
      url: resourceUrl,
      issues: parsed.error.issues.slice(0, 5),
    });
    // Return raw payload with assertion to avoid blocking caller; future: tighten schema & migrate.
    // eslint-disable-next-line no-restricted-syntax
    return json as z.infer<Z>;
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// S3 Signed URL Resolution
// ---------------------------------------------------------------------------
// During migration we may receive resource metadata lacking a pre-signed URL
// (only a fileKey). In that case we request a one-off signed URL from the
// upload service. This preserves backwards compatibility with the legacy
// Cloud Function that returned direct CloudFront links.

async function fetchSignedUrlForFileKey(
  fileKey: string,
): Promise<string | null> {
  try {
    const signUrl = `${
      process.env.REACT_APP_API_URL
    }/api/upload/signed-url?fileKey=${encodeURIComponent(fileKey)}`;
    const resp = await fetch(signUrl, { method: 'GET' });
    if (!resp.ok) {
      logger.warn('Failed to get signed URL for fileKey', {
        fileKey,
        status: resp.status,
      });
      return null;
    }
    const json = await resp.json();
    const data: unknown = json?.data;
    if (data && typeof data === 'object' && 'url' in data) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const potentialUrl = (data as { [k: string]: unknown })['url'];
      if (typeof potentialUrl === 'string') return potentialUrl;
    }
    logger.warn('Signed URL response missing url field', { fileKey });
    return null;
  } catch (error) {
    logger.error('Exception while requesting signed URL', { fileKey, error });
    return null;
  }
}

async function resolveSnapshotUrl(
  rec: VideoResourceSchemaType,
): Promise<string | null> {
  const maybeUrl = rec.url;
  if (typeof maybeUrl === 'string' && maybeUrl.length > 0) return maybeUrl;
  const maybeFileKey = rec.fileKey;
  if (typeof maybeFileKey === 'string' && maybeFileKey.length > 0) {
    return fetchSignedUrlForFileKey(maybeFileKey);
  }
  return null;
}

export async function requestVideoResources(
  storyId: string,
  videoId: string,
  videoVersionId: string | 'latest',
  fields: VideoResourceField[],
): Promise<VideoResources> {
  const query = new URLSearchParams({
    storyId,
    videoId,
    versionId: videoVersionId,
    fields: fields.join(','),
  }).toString();
  const url = `${process.env.REACT_APP_API_URL}/api/video/version-resources?${query}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (response.ok) {
    const responseJson = await response.json();
    if (!responseJson.data) {
      throw new Error('Response data is empty');
    }

    const parsed = VideoResourcesSchema.safeParse(responseJson.data);
    if (!parsed.success) {
      logger.warn('VideoResources validation failed', {
        issues: parsed.error.issues.slice(0, 5),
      });
      // Fallback: return raw data cast (best-effort). This preserves backward compatibility.
      // eslint-disable-next-line no-restricted-syntax
      return responseJson.data as VideoResources;
    }
    const shaped: VideoResources = {};

    // Resolve potentially absent signed URLs (fileKey only cases)
    if (parsed.data.videoSourceSnapshot) {
      const vs = parsed.data.videoSourceSnapshot;
      // eslint-disable-next-line no-await-in-loop -- sequential resolution acceptable (3 items max)
      const resolvedUrl = await resolveSnapshotUrl(vs);
      if (resolvedUrl) {
        shaped.videoSourceSnapshot = { url: resolvedUrl };
      }
    }
    if (parsed.data.transcriptionSnapshot) {
      const ts = parsed.data.transcriptionSnapshot;
      // eslint-disable-next-line no-await-in-loop
      const resolvedUrl = await resolveSnapshotUrl(ts);
      if (resolvedUrl) {
        shaped.transcriptionSnapshot = { url: resolvedUrl };
      }
    }
    if (parsed.data.karaokeData) {
      const kd = parsed.data.karaokeData;
      // eslint-disable-next-line no-await-in-loop
      const resolvedUrl = await resolveSnapshotUrl(kd);
      if (resolvedUrl) {
        shaped.karaokeData = { url: resolvedUrl };
      }
    }
    return shaped;
  } else {
    console.error('Failed to fetch cloudfront links');
    return {};
  }
}

type PopulatableVideo = Pick<Video, 'id'> & {
  videoSource?: Video['videoSource'];
  videoJson?: Video['videoSource'];
  versionId?: string;
  transcriptionSnapshot?: Video['transcriptionSnapshot'];
  transcriptionChanges?: Video['transcriptionChanges'];
  karaokeData?: Video['karaokeData'];
};

type PopulatedVideo<
  V extends PopulatableVideo,
  R extends PopulatableProperties[],
> = V & {
  [K in R[number]]: NonNullable<V[K]>;
};

export type VideoResourceField =
  | 'videoSourceSnapshot'
  | 'transcriptionSnapshot'
  | 'karaokeData';
type PopulatableProperties =
  | 'videoSource'
  | 'transcriptionSnapshot'
  | 'karaokeData';

function ensureVideoHasResources<
  V extends PopulatableVideo,
  R extends PopulatableProperties[],
>(video: V, populatedProperties: R): asserts video is PopulatedVideo<V, R> {
  if (populatedProperties.includes('videoSource') && !video.videoSource) {
    throw new Error('Invariant: videoSource expected to be populated');
  }
  if (
    populatedProperties.includes('transcriptionSnapshot') &&
    !video.transcriptionSnapshot
  ) {
    throw new Error(
      'Invariant: transcriptionSnapshot expected to be populated',
    );
  }
  if (populatedProperties.includes('karaokeData') && !video.karaokeData) {
    throw new Error('Invariant: karaokeData expected to be populated');
  }
}

export async function populateVideoVersionWithResources<
  V extends PopulatableVideo,
>(
  storyId: string,
  video: V,
  resourceFields: VideoResourceField[],
  skipIfAlreadyPopulated: boolean = true,
  versionId: string | 'latest' = video.versionId || 'latest',
): Promise<PopulatedVideo<V, PopulatableProperties[]>> {
  let populatedProperties: PopulatableProperties[] = [];
  if (resourceFields.includes('videoSourceSnapshot')) {
    if (video.videoSource && skipIfAlreadyPopulated) {
      logger.debug(
        'VideoSourceProvider | video source already populated, skipping fetch',
      );
      populatedProperties.push('videoSource');
    }
    if (
      !populatedProperties.includes('videoSource') &&
      video.videoJson &&
      skipIfAlreadyPopulated
    ) {
      logger.debug('VideoSourceProvider | taking from videoJson');
      video.videoSource = video.videoJson;
      populatedProperties.push('videoSource');
    }
    if (!populatedProperties.includes('videoSource') && !video.id) {
      throw new Error(
        'populateVideoVersionWithResources: video.id is required to fetch video source',
      );
    }
  }

  if (resourceFields.includes('transcriptionSnapshot')) {
    // DO NOT SKIP FETCH
    // if (video.transcriptionSnapshot && skipIfAlreadyPopulated) {
    //   logger.debug(
    //     'VideoSourceProvider | transcription snapshot already populated, skipping fetch',
    //   );
    //   populatedProperties.push('transcriptionSnapshot');
    // }
    if (!populatedProperties.includes('transcriptionSnapshot') && !video.id) {
      throw new Error(
        'populateVideoVersionWithResources: video.id is required to fetch transcription',
      );
    }
  }

  if (resourceFields.includes('karaokeData')) {
    if (video.karaokeData && skipIfAlreadyPopulated) {
      logger.debug(
        'VideoSourceProvider | karaokeData already populated, skipping fetch',
      );
      populatedProperties.push('karaokeData');
    }
    if (!populatedProperties.includes('karaokeData') && !video.id) {
      throw new Error(
        'populateVideoVersionWithResources: video.id is required to fetch karaokeData',
      );
    }
  }

  if (populatedProperties.includes('videoSource')) {
    resourceFields = resourceFields.filter(f => f !== 'videoSourceSnapshot');
  }

  if (populatedProperties.includes('transcriptionSnapshot')) {
    resourceFields = resourceFields.filter(f => f !== 'transcriptionSnapshot');
  }

  if (populatedProperties.includes('karaokeData')) {
    resourceFields = resourceFields.filter(f => f !== 'karaokeData');
  }

  if (resourceFields.length > 0 && video.id) {
    const resources = await requestVideoResources(
      storyId,
      video.id,
      versionId,
      resourceFields,
    );

    if (resourceFields.some(field => !resources[field]?.url)) {
      throw new Error(
        `Failed to fetch requested video resources: ${resourceFields
          .filter(field => !resources[field]?.url)
          .join(', ')}`,
      );
    }

    if (resources.transcriptionSnapshot?.url) {
      video.transcriptionSnapshot = await fetchJsonResourceFromUrl(
        resources.transcriptionSnapshot.url,
        VideoTranscriptSnapshotSchema,
      );
    }
    if (resources.videoSourceSnapshot?.url) {
      video.videoSource = await fetchJsonResourceFromUrl(
        resources.videoSourceSnapshot.url,
        VideoSourceSchema,
      );
    }
    if (resources.karaokeData?.url) {
      const karaokeDataSerialized: KaraokeDataSerialized =
        await fetchJsonResourceFromUrl(
          resources.karaokeData.url,
          VideoKaraokeDataSchema,
        );
      video.karaokeData = {
        ...karaokeDataSerialized,
        configs: new Map(karaokeDataSerialized.configs),
      };
    }

    logger.debug('Populated video resources', {
      resourceFields,
      videoId: video.id,
    });
  }

  ensureVideoHasResources(video, populatedProperties);
  return video;
}

export interface SaveVideoResourceOptions {
  /** Enable writing to new S3 backend (single write). */
  useS3?: boolean;
  /** When true: write to BOTH legacy endpoint and S3 (migration shadow / dual write). */
  dualWrite?: boolean;
}

const VideoResourceMetadataSchema = z
  .object({
    fileKey: z.string(),
    fileName: z.string(),
    md5Hash: z.string(),
  })
  .passthrough();

async function legacySaveVideoResource(
  storyId: string,
  resource: object,
  resourceField: VideoResourceField,
): Promise<VideoResourceMetadata | null> {
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/video/version-resource`;
    const resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        storyId,
        resourceContentJson: JSON.stringify(resource, null, 2),
        resourceField,
      }),
    });
    if (!resp.ok) {
      logger.warn('Legacy save failed', {
        storyId,
        resourceField,
        status: resp.status,
      });
      return null;
    }
    const json = await resp.json();
    const parsed = VideoResourceMetadataSchema.safeParse(json.data);
    if (parsed.success) {
      return parsed.data;
    } else {
      logger.warn('Legacy save response validation failed', {
        storyId,
        resourceField,
        issues: parsed.error.issues.slice(0, 5),
      });
      return null;
    }
  } catch (e) {
    logger.error('Legacy save threw exception', {
      storyId,
      resourceField,
      error: e,
    });
    return null;
  }
}

export async function saveVideoResources(
  storyId: string,
  resources: Partial<Record<VideoResourceField, object>>,
  options: { useS3?: boolean; dualWrite?: boolean } = {},
): Promise<Partial<Record<VideoResourceField, VideoResourceMetadata | null>>> {
  for (const key of Object.keys(resources) as VideoResourceField[]) {
    if (!resources[key]) {
      throw new Error(`Resource for field ${key} is not set`);
    }
  }

  const videoResourcesMetadata = await Promise.all(
    Object.entries(resources).map(
      async ([field, data]) =>
        await saveVideoResource(
          storyId,
          data,
          field as VideoResourceField,
          options,
        ),
    ),
  );

  if (!videoResourcesMetadata) {
    throw new Error('Failed to save video resources metadata');
  }

  return videoResourcesMetadata.reduce<
    Partial<Record<VideoResourceField, VideoResourceMetadata | null>>
  >((acc, curr, index) => {
    const key = Object.keys(resources)[index] as VideoResourceField;
    acc[key] = curr;
    return acc;
  }, {});
}

export async function saveVideoResource(
  storyId: string,
  resource: object,
  resourceField: VideoResourceField,
  options: SaveVideoResourceOptions = {},
): Promise<VideoResourceMetadata | null> {
  // ------------------------------------------------------------------
  // New implementation: persist JSON snapshot to S3 directly using the
  // existing multipart upload endpoints (/api/upload/*) consumed by
  // Uploader (initialize -> pre-sign-urls -> PUT parts -> finalize).
  // We locally compute the md5 hash so metadata matches former
  // Cloud Function response shape { fileKey, fileName, md5Hash }.
  // Assumption: backend requestVideoResources builds CloudFront URLs
  // from stored fileKey; thus we pick a deterministic key prefix.
  // ------------------------------------------------------------------

  const { useS3 = true, dualWrite = false } = options;

  try {
    // If dualWrite requested, kick off legacy path in parallel (fire-and-forget) while we proceed with S3.
    let legacyPromise: Promise<VideoResourceMetadata | null> | undefined;
    if (dualWrite) {
      legacyPromise = legacySaveVideoResource(storyId, resource, resourceField);
    } else if (!useS3) {
      // Legacy only mode
      return legacySaveVideoResource(storyId, resource, resourceField);
    }

    const jsonString = JSON.stringify(resource, null, 2);
    const md5Hash = MD5(jsonString).toString();

    // Use a stable folder prefix so backend can serve it later.
    // Pattern: video-resources/<storyId>/<resourceField>/<uuid>.json
    const fileName = `${resourceField}-${new Date()
      .toISOString()
      .replace(/[:.]/g, '-')}.json`;
    const fileKey = `sessions/${storyId}/resources/${fileName}`;

    const file = new File([jsonString], fileName, {
      type: 'application/json',
      lastModified: Date.now(),
    });

    await new Promise<void>((resolve, reject) => {
      const uploader = new Uploader({
        file,
        fileName,
        fileKey,
        baseURL: `${process.env.REACT_APP_API_URL}/api/upload`,
        chunkSize: 10, // MB (min 5MB enforced by uploader)
        threadsQuantity: 1,
        useTransferAcceleration: true,
        onCompleteFn: () => resolve(),
        onError: error => reject(error),
      });
      uploader.start();
    });

    const metadata: VideoResourceMetadata = {
      fileKey,
      fileName,
      md5Hash,
    };

    logger.debug(`Saved ${resourceField} to S3 via multipart upload`, {
      metadata,
      storyId,
      dualWrite,
    });

    if (legacyPromise) {
      legacyPromise.then(legacyMeta => {
        if (!legacyMeta) {
          logger.warn('Dual write legacy path failed', {
            storyId,
            resourceField,
          });
        }
      });
    }
    return metadata;
  } catch (error) {
    logger.error('Failed to save video resource to S3', {
      storyId,
      resourceField,
      error,
    });
    // Fallback: attempt legacy save if S3 failed but we intended to use S3.
    if (useS3) {
      logger.warn('Attempting legacy fallback save after S3 failure', {
        storyId,
        resourceField,
      });
      return legacySaveVideoResource(storyId, resource, resourceField);
    }
    return null;
  }
}
